import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/AuthCard";
import { OtpInput } from "@/components/auth/OtpInput";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const RESEND_COOLDOWN_SECONDS = 30;

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const nextRaw = params.get("next");
  const next = nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : null;
  const navigate = useNavigate();
  const { confirm, resend } = useAuth();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!email) {
    return (
      <AuthCard title="Verify your email" description="We need an email to send the code to.">
        <Button fullWidth asChild>
          <Link to="/auth/signup">Go to sign up</Link>
        </Button>
      </AuthCard>
    );
  }

  const handleSubmit = async () => {
    if (code.length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await confirm(email, code);

      toast.success("Email verified. Sign in to continue.");
      const loginHref = next ? `/auth/login?next=${encodeURIComponent(next)}` : "/auth/login";
      navigate(loginHref, { replace: true });
    } catch (err) {
      const message = (err as { message?: string }).message || "Verification failed.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resend(email);
      toast.success("New code sent.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const message = (err as { message?: string }).message || "Could not resend.";
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthCard
      title="Verify your email"
      description={`We sent a 6-digit code to ${email}. Enter it below.`}
      footer={
        <>
          Wrong email?{" "}
          <Link to="/auth/signup" className="font-medium text-brand hover:underline">
            Go back
          </Link>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <OtpInput
          value={code}
          onChange={(v) => {
            setCode(v);
            setError(null);
          }}
          autoFocus
          inputMode="numeric"
        />
        {error ? <p className="text-sm text-danger -mt-2">{error}</p> : null}

        <Button onClick={handleSubmit} fullWidth loading={submitting}>
          Verify
        </Button>

        <div className="flex items-center justify-between text-sm">
          <span className="text-text-muted">Didn't get the code?</span>
          <button
            type="button"
            disabled={resending || cooldown > 0}
            onClick={handleResend}
            className="text-brand hover:underline disabled:text-text-subtle disabled:no-underline disabled:cursor-not-allowed"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Resending..." : "Resend"}
          </button>
        </div>
      </div>
    </AuthCard>
  );
}
